<template>
  <div class="tw-pb-4 lg:tw-pb-0">
    <EntityDetailSkeleton v-if="isLoading" />

    <!-- Header -->
    <EntityDetailHeader
      v-if="!isLoading"
      :eyebrow="t('orders.recurring.recurring_label')"
      :title="order.recurring_number || t('orders.titles.order_details')"
      :has-prev="Boolean(order?.previous_recurring_instruction?.id)"
      :has-next="Boolean(order?.next_recurring_instruction?.id)"
      :prev-label="t('common.forms.previous')"
      :next-label="t('common.forms.next')"
      @prev="navigateToOrder(order?.previous_recurring_instruction?.id)"
      @next="navigateToOrder(order?.next_recurring_instruction?.id)"
    >
      <template #status>
        <Badge :variant="getStatusBadgeVariant()">{{ getOrderStatusDisplay() }}</Badge>
        <Badge v-if="order.is_clarification_requested" variant="clarification">
          {{ t('orders.titles.clarification_requested') }}
        </Badge>
      </template>

      <template #actions>
        <Button v-if="showEditOrderBtn()" variant="outline" size="sm" @click="editOrder(order.id)">
          <Pencil class="tw-h-4 tw-w-4" />
          {{ t('common.forms.edit') }}
        </Button>

        <CustomerActionButton
          v-if="showApproveOrRejectAction()"
          :loading="isApproving"
          :class="[actionButtonClass, 'tw-bg-emerald-600 tw-text-white hover:tw-bg-emerald-700']"
          @click="approveSingleOrder(order.id)"
        >
          <CheckCircle class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.approve') }}
        </CustomerActionButton>
        <CustomerActionButton
          v-if="showApproveOrRejectAction()"
          :loading="isRejecting"
          :class="[actionButtonClass, 'tw-bg-red-500 tw-text-white hover:tw-bg-red-500/90']"
          @click="rejectOrder(order.id)"
        >
          <XCircle class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.reject') }}
        </CustomerActionButton>
        <CustomerActionButton
          v-if="showRequestClarificationAction()"
          :class="[
            actionButtonClass,
            'tw-border tw-border-amber-500 tw-text-amber-600 hover:tw-bg-amber-50 dark:tw-text-amber-400',
          ]"
          @click="showClarificationDialog = true"
        >
          <MessageCircleQuestion class="tw-h-4 tw-w-4" />
          {{ t('requests.actions.request_clarification') }}
        </CustomerActionButton>

        <DropdownMenu
          v-if="
            showRespondToClarificationAction() ||
            (order.status === 'DRAFT' && order.released_by?.id === currentUser?.id)
          "
        >
          <DropdownMenuTrigger as-child>
            <Button variant="outline" size="sm">
              {{ t('common.forms.actions') }}
              <ChevronDown class="tw-h-4 tw-w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="tw-w-60">
            <DropdownMenuItem
              v-if="showRespondToClarificationAction()"
              @click="respondToClarification(order.id)"
            >
              <CheckCircle2 class="tw-mr-2 tw-h-4 tw-w-4 tw-text-surface-500" />
              {{ t('orders.actions.mark_clarifications_completed') }}
            </DropdownMenuItem>
            <template v-if="order.status === 'DRAFT' && order.released_by?.id === currentUser?.id">
              <DropdownMenuSeparator />
              <DropdownMenuItem
                class="tw-text-red-600 focus:tw-text-red-600 dark:tw-text-red-400"
                @click="isDeleteDialogOpen = true"
              >
                <Trash2 class="tw-mr-2 tw-h-4 tw-w-4" />
                {{ t('common.forms.delete') }}
              </DropdownMenuItem>
            </template>
          </DropdownMenuContent>
        </DropdownMenu>
      </template>
    </EntityDetailHeader>

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:open="isDeleteDialogOpen">
      <DialogContent class="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>{{ t('common.forms.delete') }}</DialogTitle>
          <DialogDescription>{{ t('orders.titles.confirm_delete') }}?</DialogDescription>
        </DialogHeader>
        <DialogFooter class="tw-flex tw-gap-3 sm:tw-justify-end">
          <Button variant="outline" @click="isDeleteDialogOpen = false">
            {{ t('common.forms.cancel') }}
          </Button>
          <Button variant="destructive" @click="onChangeOrderDelete">
            {{ t('common.forms.delete') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Clarification Request Dialog -->
    <Dialog v-model:open="showClarificationDialog">
      <DialogContent class="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>{{ t('common.forms.confirmation') }}</DialogTitle>
          <DialogDescription>
            {{ t('orders.titles.confirm_clarification') }}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          v-model="clarificationComment"
          :placeholder="t('orders.titles.message_hint')"
          class="tw-min-h-[120px]"
        />
        <DialogFooter class="tw-flex tw-gap-3 sm:tw-justify-end">
          <Button variant="outline" @click="showClarificationDialog = false">
            {{ t('common.forms.cancel') }}
          </Button>
          <Button @click="clarificationOrder(order.id)">
            {{ t('requests.actions.request_clarification') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <!-- Main Content -->
    <EntitySplitLayout
      v-if="!isLoading"
      storage-key="recurring-order-split-document-size"
      :min-document-size="50"
      :max-document-size="67"
    >
      <template #documents>
        <DocumentManagerPanel :files="order.files || []" :show-heading="false" />
      </template>
      <template #details>
        <EntityDetailTabs :show-related="false">
          <template #details>
            <RecurringPurchase :order="order" />
            <RelatedOrders v-if="order?.id" :recurring-instruction-id="order?.id" />
          </template>
          <template #comments>
            <Comments :recurring-instruction="order" />
          </template>
          <template #notes>
            <SelfNotes :recurring-instruction="order" />
          </template>
        </EntityDetailTabs>
      </template>
    </EntitySplitLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user.store';
import { storeToRefs } from 'pinia';
import { useRecurringInstructionStore } from '@/stores/recurring-instructions.store';
import { useI18n } from 'vue-i18n';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';

// Components
import SelfNotes from '@/components/order-details/self-notes/SelfNotes.vue';
import Comments from '@/components/order-details/comments/Comments.vue';
import RelatedOrders from '@/components/order-details/related-orders/RelatedOrders.vue';
import RecurringPurchase from '@/components/order-details/recurring-purchase/RecurringPurchase.vue';
import { DocumentManagerPanel } from '@/components/shared/documents';
import EntityDetailTabs from '@/components/shared/entity-detail/EntityDetailTabs.vue';
import EntitySplitLayout from '@/components/shared/entity-detail/EntitySplitLayout.vue';
import EntityDetailHeader from '@/components/shared/entity-detail/EntityDetailHeader.vue';
import EntityDetailSkeleton from '@/components/shared/entity-detail/EntityDetailSkeleton.vue';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';

// UI Components
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Icons
import {
  ChevronDown,
  Pencil,
  Trash2,
  CheckCircle,
  CheckCircle2,
  XCircle,
  MessageCircleQuestion,
} from 'lucide-vue-next';

// Shared styling for the inline header action buttons (matches shadcn `size="sm"`).
const actionButtonClass =
  'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-h-8 tw-px-3 tw-text-xs tw-font-medium tw-shadow-sm tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-neutral-950 dark:focus-visible:tw-ring-neutral-300';

const router = useRouter();
const {
  getRecurringInstructionById,
  approveRecurringInstruction,
  rejectRecurringInstruction,
  requestRecurringInstructionClarification,
  respondToRecurringInstructionClarification,
} = useRecurringInstructionStore();

const { currentUser } = storeToRefs(useUserStore());

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { setBreadcrumbs } = useBreadcrumbs();

const order = ref<any>({
  id: '',
  recurring_number: '',
  recurring_number_without_prefix: '',
  customer_name: '',
  customer: '',
  supplier: '',
  supplier_name: '',
  project: '',
  project_name: '',
  document_no_internal: null,
  name: '',
  frequency: '',
  service_date: '',
  start_date: '',
  end_after: '',
  current_balance: '',
  amount: '',
  created_on: '',
  created_by_name: '',
  created_by: '',
  released_by_name: '',
  released_by: '',
  status: '',
  files: [],
});

const orderId = ref<number | null>(null);
const isDeleteDialogOpen = ref(false);
const isApproving = ref(false);
const isRejecting = ref(false);
const showClarificationDialog = ref(false);
const clarificationComment = ref('');

const navigateToOrder = (id: number | null) => {
  if (id) {
    router.push({ name: 'recurring-order', params: { id } });
  }
};

const editOrder = (id: number) => {
  router.push({ name: 'edit-recurring-order', params: { id } });
};

const approveSingleOrder = async (id: number) => {
  isApproving.value = true;
  try {
    await handleApiError(async () => {
      await approveRecurringInstruction(id);
      order.value = await getRecurringInstructionById(id);
    }, t('orders.recurring.successful_recurring_instruction_approved'));
  } finally {
    isApproving.value = false;
  }
};

const clarificationOrder = async (id: number) => {
  await handleApiError(async () => {
    await requestRecurringInstructionClarification(id, clarificationComment.value);
    order.value = await getRecurringInstructionById(id);
    showClarificationDialog.value = false;
    clarificationComment.value = '';
  }, t('orders.recurring.successful_clarification_requested'));
};

const respondToClarification = async (id: number) => {
  await handleApiError(async () => {
    await respondToRecurringInstructionClarification(id);
    order.value = await getRecurringInstructionById(id);
  }, t('orders.recurring.successful_clarification_completed'));
};

const rejectOrder = async (id: number) => {
  isRejecting.value = true;
  try {
    await handleApiError(async () => {
      await rejectRecurringInstruction(id);
      order.value = await getRecurringInstructionById(id);
    }, t('orders.recurring.successful_recurring_instruction_rejected'));
  } finally {
    isRejecting.value = false;
  }
};

const onChangeOrderDelete = async () => {
  isDeleteDialogOpen.value = false;
  router.push({ name: 'orders' });
};

const getStatusBadgeVariant = (): BadgeVariant => {
  const statusMap: Record<string, BadgeVariant> = {
    APPROVED: 'approved',
    CLEARING: 'clearing',
    REJECTED: 'rejected',
    DRAFT: 'draft',
    PENDING_APPROVAL: 'pending',
  };
  return statusMap[order.value.status_display] || 'secondary';
};

const getOrderStatusDisplay = () => {
  if (order.value.status_display === 'APPROVED') {
    return t('orders.titles.approved');
  } else if (order.value.status_display === 'REJECTED') {
    return t('orders.titles.rejected');
  } else if (order.value.status_display === 'DRAFT') {
    return t('orders.titles.draft');
  } else if (order.value.status_display === 'PENDING_APPROVAL') {
    return t('requests.titles.pending');
  } else if (order.value.status_display === 'CLEARING' && !order.value.is_restrict) {
    return t('orders.titles.clearing');
  } else if (order.value.status_display === 'CLEARING' && order.value.is_restrict) {
    return `${t('orders.titles.clearing')} (${order.value.total_clearer_approved || 0} / ${
      order.value.total_clearer || 0
    })`;
  }
  return '';
};

const showEditOrderBtn = () => {
  return (
    (order.value.status === 'DRAFT' && order.value.released_by?.id === currentUser.value?.id) ||
    (order.value.is_clarification_requested && order.value.created_by === currentUser.value?.id)
  );
};

const showApproveOrRejectAction = () => {
  if (currentUser.value?.user_role.includes('Admin')) {
    return ['PENDING_APPROVAL', 'CLEARING'].includes(order.value.status_display);
  }
  if (currentUser.value?.user_role.includes('Approver')) {
    return order.value.status_display === 'PENDING_APPROVAL';
  }
  if (currentUser.value?.user_role.includes('Order Clearer')) {
    return order.value.status_display === 'PENDING_APPROVAL';
  }
  return false;
};

const showRequestClarificationAction = () => {
  if (
    currentUser.value?.user_role.includes('Admin') ||
    currentUser.value?.user_role.includes('Approver') ||
    currentUser.value?.user_role.includes('Order Clearer')
  ) {
    return (
      ['PENDING_APPROVAL', 'CLEARING'].includes(order.value.status_display) &&
      !order.value.is_clarification_requested
    );
  }
  return false;
};

const showRespondToClarificationAction = () => {
  return (
    order.value.is_clarification_requested &&
    (currentUser.value?.id === order.value.created_by ||
      currentUser.value?.id === order.value.requested_clarification_by_id)
  );
};

const isLoading = ref(true);

onMounted(async () => {
  orderId.value = +router.currentRoute.value.params.id;
  if (router.currentRoute.value.name === 'recurring-order' && isNaN(+orderId.value)) {
    return router.push({ name: 'recurring-orders' });
  }
  try {
    order.value = await getRecurringInstructionById(orderId.value);

    setBreadcrumbs([
      {
        labelKey: 'sidebar.items.recurring_orders',
        to: { name: 'recurring-orders' },
      },
      {
        label: order.value?.recurring_number || '',
      },
    ]);
  } finally {
    isLoading.value = false;
  }
});

watch(
  () => router.currentRoute.value.params.id,
  async newVal => {
    if (router.currentRoute.value.name === 'recurring-order' && isNaN(+newVal)) {
      return router.push({ name: 'recurring-orders' });
    } else if (router.currentRoute.value.name === 'recurring-order') {
      order.value = await getRecurringInstructionById(newVal.toString());

      setBreadcrumbs([
        {
          labelKey: 'sidebar.items.recurring_orders',
          to: { name: 'recurring-orders' },
        },
        {
          label: order.value?.recurring_number || '',
        },
      ]);
    }
  }
);
</script>
